import { Box, Divider, HStack, Skeleton, Stack, Text } from "@chakra-ui/react";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
} from "gql/graphql";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import colors from "app/colors";
import FileThumb from "modules/files/file-thumb";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import MessageHtml from "./message-html";
import MessageFile from "./message-file";
import { MESSAGE_STATE } from "modules/utils/Constants";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  messageState: MESSAGE_STATE;
}

const MAX_HTML_PREVIEW_HEIGHT = 400;
const PDF_WIDTH = 482;
const PDF_HEIGHT = 330;

type EmailHtmlState =
  | { status: "loading" }
  | { status: "loaded"; html: string }
  | { status: "none" };

const EmailMessage: React.FC<IProps> = ({
  messageThread,
  message,
  messageState,
}) => {
  const htmlPreviewRef = useRef<HTMLDivElement>(null);

  const { openModal } = useOverlay();

  const openGallery = useCallback(
    (fileId: string) => {
      openModal({
        type: MODAL_TYPE.MESSAGE_THREAD_FILE_GALLERY,
        id: message.messageThreadId,
        id2: fileId,
      });
    },
    [message.messageThreadId, openModal],
  );
  const [isOverflowing, setIsOverflowing] = useState(false);
  // reserve the preview box height while the html loads so the message item
  // mounts at a stable size and the list doesn't reflow under the scroll anchor
  const [htmlState, setHtmlState] = useState<EmailHtmlState>(() =>
    message.htmlCloudFrontUrl ? { status: "loading" } : { status: "none" },
  );

  useEffect(() => {
    const htmlCloudFrontUrl = message.htmlCloudFrontUrl;
    if (!htmlCloudFrontUrl) {
      setHtmlState({ status: "none" });
      return;
    }

    setHtmlState({ status: "loading" });
    const abortController = new AbortController();
    fetch(htmlCloudFrontUrl, {
      signal: abortController.signal,
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.text())
      .then((html) => {
        setHtmlState({ status: "loaded", html });
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setHtmlState({ status: "none" });
      });

    return () => {
      abortController.abort();
    };
  }, [message.htmlCloudFrontUrl]);

  useEffect(() => {
    if (!htmlPreviewRef.current) {
      return;
    }
    if (htmlState.status !== "loaded") {
      setIsOverflowing(false);
      return;
    }
    setIsOverflowing(
      htmlPreviewRef.current.clientHeight >= MAX_HTML_PREVIEW_HEIGHT,
    );
  }, [htmlState]);

  const messagePdfFile = message.htmlPdfFile;

  if (!messagePdfFile || message.type !== MessageType.Email) {
    return null;
  }

  return (
    <Stack spacing={2} pb={3} w="full">
      <Box
        ref={htmlPreviewRef}
        pos="relative"
        maxH={MAX_HTML_PREVIEW_HEIGHT}
        overflow="hidden"
        sx={{
          WebkitMaskImage: isOverflowing
            ? "linear-gradient(to bottom, rgba(0, 0, 0, 1) 70%, rgba(0, 0, 0, 0) 100%)"
            : "none",
          maskImage: isOverflowing
            ? "linear-gradient(to bottom, rgba(0, 0, 0, 1) 70%, rgba(0, 0, 0, 0) 100%)"
            : "none",
          maskRepeat: "no-repeat",
          maskSize: "100% 100%",
        }}
      >
        {htmlState.status === "loading" && (
          <Skeleton h={`${MAX_HTML_PREVIEW_HEIGHT}px`} w="full" />
        )}
        {htmlState.status === "loaded" && <MessageHtml html={htmlState.html} />}
      </Box>

      <HStack w="full" pt={4}>
        <Text w={165} color={colors.gray50} fontSize={12} fontWeight={700}>
          Email Message (PDF)
        </Text>
        <Divider borderColor={colors.gray30} borderStyle="dashed" />
      </HStack>

      <Box
        w={PDF_WIDTH}
        cursor="pointer"
        onClick={() => {
          openGallery(messagePdfFile.id);
        }}
      >
        <MessageFile
          message={message}
          messageThread={messageThread}
          file={messagePdfFile}
          messageState={messageState}
        >
          <FileThumb
            contentType={messagePdfFile.contentType}
            name={messagePdfFile.name}
            url={messagePdfFile.previewCloudFrontUrl}
            size={[PDF_WIDTH, PDF_HEIGHT]}
            objectFit="contain"
          />
        </MessageFile>
      </Box>
    </Stack>
  );
};

export default memo(EmailMessage);
